<template>
  <v-dialog
    hide-overlay
    :overlay-opacity="0"
    transition="fade-transition"
    persistent
    ref="dialog"
    :value="isOpen ? true : false"
    absolute
  >
    <div class="card-overlay" @click="$refs.dialog.animateClick()"/>

    <v-card style="width: 600px" class="" v-if="isOpen">
      <template v-if="isOpen === 'verify-domain'">
        <v-card-title class="pb-0 flex justify-space-between align-center">
          <h4>
            {{ $t("heading.onboarding.transferDomain.title") }}
          </h4>
          <v-btn x-small icon @click="$emit('removeModal')" class="close-model">
            <v-icon>$close</v-icon>
          </v-btn>
        </v-card-title>

        <template v-if="selectedService.onboarding.domain_transfer_enabled">
          <v-card-text height="800px" class="px-10 pt-8">
            <h6 class="p-2 font-weight-light">
              <i18next
                :translation="$t('heading.onboarding.transferDomain.subtitle')"
              >
                <template #domain><span class="font-weight-600">{{ domain }}</span></template>
              </i18next>
            </h6>

            <p class="p-3 mt-2">
              <i18next
                :translation="$t('heading.onboarding.transferDomain.message.possible')"
              >
                <template #recordType><span class="font-weight-800">{{ domainVerificationRecord.type }}</span>
                </template>
                <template #domain><span class="font-weight-800">{{ domain }}</span></template>
              </i18next>
            </p>

            <div class="recovery-code-box">
              {{ domainVerificationRecord.content }}
              <copy-text-button
                :text="domainVerificationRecord.content"
                :color="'primary'"
                size="24"
              />
            </div>
          </v-card-text>
        </template>
        <template v-else>
          <v-card-text height="800px" class="px-10 pt-8">
            <h6 class="p-2 font-weight-light">
              <i18next
                :translation="$t('heading.onboarding.transferDomain.subtitle')"
              >
                <template #domain><span class="font-weight-600">{{ domain }}</span></template>
              </i18next>
            </h6>
            <p class="p-3 mt-2">
              {{ $t('heading.onboarding.transferDomain.message.impossible') }}
            </p>
          </v-card-text>
        </template>

        <v-card-actions class="d-flex flex-column pb-6 px-10 pt-2 mt-4">
          <template v-if="selectedService.onboarding.domain_transfer_enabled">
            <v-btn
              x-large
              elevation="0"
              color="primary"
              block
              @click="$emit('transfer-domain')"
              :loading="submitting || loading"
            >
              <span v-if="
                selectedService.install_method === 'quick'
                || (selectedService.install_method === 'super_quick' && selectedService.onboarding.super_quick_ask_for_site_name)
              "
              >{{ $t('button.website.set') }}</span>
              <span v-else>{{ $t('button.transferNow') }}</span>
              <v-icon size="24" class="ml-2">$arrowright</v-icon>
            </v-btn>
            <v-btn
              x-large
              elevation="0"
              class="mx-0 mt-2"
              color="gray"
              text
              block
              @click="$emit('removeModal')"
            >
            <span class="p-1 font-weight-light gray--text text--darken-1">
              {{ $t("button.back") }}
            </span>
            </v-btn>
          </template>
          <template v-else>
            <v-btn
              x-large
              elevation="0"
              color="primary"
              block
              @click="$emit('removeModal')"
            >{{ $t('button.dashboard.back') }}
            </v-btn>
          </template>
        </v-card-actions>
      </template>

      <template v-else-if="isOpen === 'verify-nameservers'">
        <v-card-title class="pb-0 flex justify-space-between align-center">
          <h4 class="font-weight-light">
            {{ $t('heading.onboarding.checkNameserversModal.title') }}
          </h4>
          <v-btn icon x-small @click="$emit('removeModal')">
            <v-icon size="24">$close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text height="800px" class="px-10 pt-6">
          <div class="main-warning-message pa-3">
            <h5 class="font-weight-600">
              <span v-if="isNameserversCorrected">
                <i18next
                    :translation="$t('heading.onboarding.checkNameserversModal.message.success')"
                  >
                  <template #domain><span class="font-weight-600">{{ domain }}</span></template>
                </i18next>
              </span>
              <span v-else-if="!isRetried">
                <i18next
                  :translation="$t('heading.onboarding.checkNameserversModal.message.incorrect')"
                >
                  <template #domain><span class="font-weight-600">{{ domain }}</span></template>
                </i18next>
              </span>
              <span v-else>{{ $t('heading.onboarding.checkNameserversModal.message.tryAgain') }}</span>
            </h5>
          </div>

          <div v-if="!isNameserversCorrected" class="mt-4">
            <h6 class="mb-2 p-2 font-weight-light ">{{ $t('heading.onboarding.checkNameserversModal.instruction.label') }}:</h6>
            <div>
              <ol>
                <li>
                  <span>{{ $t('heading.onboarding.checkNameserversModal.instruction.firstStep') }}</span>
                </li>
                <li>
                  <div class="mb-2">{{ $t('heading.onboarding.checkNameserversModal.instruction.secondStep') }}:</div>
                  <div class="well p-d pa-3 mt-1" v-for="(ns, index) in nameservers" :key="index">
                    <strong>{{ ns }}</strong>
                  </div>
                </li>
                <li class="mt-3">
                  <span>{{ $t('heading.onboarding.checkNameserversModal.instruction.thirdStep') }}.</span>
                </li>
              </ol>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="d-flex flex-column pb-6 px-10 pt-2 mt-4">
          <v-btn
            v-if="!isNameserversCorrected"
            x-large
            elevation="0"
            color="primary"
            block
            @click="checkNameservers"
            :loading="submitting"
          >{{ $t('button.checkNameservers') }}
          </v-btn>
          <v-btn
            v-else
            x-large
            elevation="0"
            color="primary"
            block
            @click="$emit('after-nameservers-check')"
            :loading="submitting"
          >
            <span v-if="
              selectedService.install_method ==='quick'
              || (selectedService.install_method === 'super_quick' && selectedService.onboarding.super_quick_ask_for_site_name)
            "
            >{{ $t("button.website.set") }}</span>
            <span v-else>{{ $t('button.website.create') }}</span>
            <v-icon size="24" class="ml-2">$arrowright</v-icon>
          </v-btn>
          <v-btn
            v-if="!isNameserversCorrected"
            @click="$emit('removeModal')"
            x-large
            elevation="0"
            class="mx-0 mt-2"
            color="gray"
            text
            block
          >
          <span class="p-1 font-weight-light gray--text text--darken-1">
            {{ $t('button.dashboard.back') }}
          </span>
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import CopyTextButton from "../../components/buttons/CopyTextButton.vue";
import Dns from "../../apis/Dns";

export default {
  name: "OnboardingVerificationDomainModal",
  components: {CopyTextButton},
  props: {
    isOpen: [Boolean, String],
    loading: Boolean,
    selectedService: Object,
    installationDetails: Object,
    domain: String,
    domainVerificationRecord: Object,
  },
  data() {
    return {
      submitting: false,
      isRetried: false,
      isNameserversCorrected: false,
    }
  },
  computed: {
    nameservers() {
      return this.selectedService.nameservers;
    },
  },
  methods: {
    async checkNameservers() {
      if (this.nameservers.length === 0) {
        this.isNameserversCorrected = true;
        return;
      }

      this.submitting = true;
      try {
        await new Promise(resolve => setTimeout(resolve, 2000));
        let result = await Dns.compareNSRecords(this.domain, this.nameservers);

        if (result.resolving && result.matching) {
          this.isNameserversCorrected = true;
        }

        this.isRetried = true;
      } finally {
        this.submitting = false;
      }
    },
  },
  watch: {
    isOpen: function (value) {
      value
        ? this.$store.dispatch("lockBodyScroll")
        : this.$store.dispatch("unlockBodyScroll");
    },
  },
}
</script>


<style lang="scss" scoped>
.recovery-code-box {
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  height: 60px;
  color: var(--v-primary-base);
  background: rgba(7, 192, 126, 0.05);
  border-radius: 6px;
  text-transform: uppercase;
  font-weight: $font-weight-bold;
  word-wrap: anywhere;
  padding: 8px;
  min-height: 80px !important;
}

.main-warning-message {
  background: var(--v-primary-lighten4);
  border-radius: 6px;

  h5 {
    margin: 0;
    font-size: 1.1em;
    color: var(--v-primary-base);

    strong {
      color: var(--v-primary-darken1) !important;
    }
  }
}

.v-dialog__content::v-deep {
  .v-dialog {
    overflow-y: auto;
    overflow-x: hidden;
  }
}

.close-model {
  position: absolute;
  top: -40px;
  right: -8px;
}

.v-card::v-deep {
  .v-card__text {
    -webkit-line-clamp: unset !important;
  }

  @media (max-width: 480px) {
    .v-card__text,
    .v-card__actions {
      padding-left: 24px !important;
      padding-right: 24px !important;
    }
  }
}

</style>